import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { Link } from 'react-router-dom'

/**
 * Why a creation was refused, inside the dialog that asked for it (app#296).
 *
 * ⚠⚠ THIS REPLACES A NATIVE `alert()`, AND THE CONTAINER IS HALF THE DEFECT. `alert()` halts the
 * JS thread until it is dismissed, so the page behind it is frozen and unreadable — measured on
 * prod 2026-09-07, two screen reads timed out with "page is busy" while the dialog stood — and it
 * is drawn in browser chrome, which does not look like noBGP. An inline `Alert` leaves the form
 * usable: the name the customer typed is still there, and so is the way out.
 *
 * ⚠ The Upgrade action is offered only when the router named a destination. `utilities/
 * limitRefusal.ts` vets it, so by the time it arrives here it is either an absolute `http(s)` URL
 * or a same-origin path — which is what tells the two apart below. A refusal with no destination
 * (a plain failure, or a payload without one) renders the sentence alone rather than a button
 * that goes nowhere.
 */
type Props = {
  /** The sentence to show. Nothing renders when it is empty. */
  message?: string
  /** Vetted by `limitRefusal`; absent for every failure that is not a spent allowance. */
  upgradeUrl?: string
}

export default function CreateRefusal({ message, upgradeUrl }: Readonly<Props>) {
  if (!message) return null

  // Same-origin paths stay inside the SPA; `limitRefusal` has already refused `//host` and every
  // non-http(s) scheme, so this test separates two safe shapes rather than guarding anything.
  const internal = upgradeUrl?.startsWith('/')

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={
        upgradeUrl ? (
          internal ? (
            <Button color="inherit" size="small" component={Link} to={upgradeUrl}>
              Upgrade
            </Button>
          ) : (
            <Button color="inherit" size="small" component="a" href={upgradeUrl} target="_blank" rel="noopener noreferrer">
              Upgrade
            </Button>
          )
        ) : undefined
      }
    >
      {message}
    </Alert>
  )
}
